import { ObjectHash } from 'Src/ng2/shared/services/object-hash/object-hash.service';
import { IFormattedMadlibOptions } from './../network-mid-level.models';
import { FormGroup, FormControl } from '@angular/forms';
import { reduce, camelCase } from 'lodash';
import { map, tap } from 'rxjs/operators';
import { ApiService } from './../../../shared/services/api-service/api-service';
import { Injectable } from '@angular/core';
import { Observable, of, asyncScheduler } from 'rxjs';
import {
  IRawNetworkMadlibData,
  INetworkMidLevelFilterOption,
  INetworkMidLevelFilterOptionHash,
} from '../network-mid-level.models';

/* istanbul ignore next */
@Injectable()
export class NetworkMidLevelDataService {
  private groupCache = {};
  private madlibCache;

  constructor(public apiService: ApiService, private objectHash: ObjectHash) {}

  public getNetworkGroupData$({ focus, filters, group }, filterHash: INetworkMidLevelFilterOptionHash, groupHash) {
    const modelHash = this.objectHash.createHash({ focus, filters, group });
    const formattedFocus = focus.toLowerCase();
    const formattedFilters = this.getFormattedFilters(filters, filterHash);
    const formattedGroup = groupHash[group].column_name;

    if (!this.groupCache[modelHash]) {
      // Does a proxy make more sense here? Or is the naive solution sufficient for now?
      if (Object.keys(this.groupCache).length > 10) this.groupCache = {};

      return this.apiService
        .getStudentsGraphQL(this.getNetworkGroupDataPayload(formattedFocus, formattedFilters, formattedGroup))
        .pipe(
          map(({ data: { NetworkMidLevelGrouping } }) => NetworkMidLevelGrouping),
          tap(groupData => (this.groupCache[modelHash] = groupData)),
        );
    } else {
      return of(this.groupCache[modelHash], asyncScheduler);
    }
  }

  getNetworkMadlibData$(): Observable<IRawNetworkMadlibData> {
    if (!this.madlibCache) {
      return this.apiService.getStudentsGraphQL(this.getNetworkMadlibQuery()).pipe(
        map(({ data: { NetworkMidLevelMadlib } }) => NetworkMidLevelMadlib),
        tap(madlibData => (this.madlibCache = madlibData)),
      );
    } else return of(this.madlibCache, asyncScheduler);
  }

  getNetworkMadlibQuery() {
    const query = `{
      NetworkMidLevelMadlib {
        fociOptions {
          key
          label
          category
          category_order
          focus_order
          sort
        }
        filterOptions{
          key
          label
          filter_order
          category
          filter_type
          filter_logic
          column_name
          filter_values
        }
        groupOptions {
          key
          column_name
          label
          group_order
          category
          category_order
        }
      }
    }`;
    return { query, fetchPolicy: 'no-cache' };
  }

  public getFormattedFilters(filters: { [key: string]: Array<string> }, filtersHash: INetworkMidLevelFilterOptionHash) {
    return reduce(
      filters,
      (acc, userSelection, key) => {
        const { filter_type, column_name, filter_values, filter_logic } = filtersHash[key];
        // If everything is selected it is equivalant to no filter at all
        if (!userSelection || (filter_type === 'multiselect' && userSelection.length === filter_values.length))
          return acc;
        else acc.push({ key, column_name, filter_type, filter_logic, values: userSelection });
        return acc;
      },
      [],
    );
  }

  // We should reconsider how we are storing those filters in PG and remove this complexity
  // And currently the hash can't reference the column bc params don't map to a column so the pg value is null
  public getDefaultFilters(
    filters_to_apply: string,
    filterOptionHash: { [key: string]: INetworkMidLevelFilterOption },
  ) {
    const filterStrings = filters_to_apply.split('AND');
    return filterStrings.reduce((acc, filterStr: string) => {
      const inClause = /\b(in)\b/;
      const extractFromInParens = /\(([^)]+)\)/;
      if (filterStr.match(inClause)) {
        const [filterColumn] = filterStr.split(inClause);
        // Why camelcase? bc the filters_to_apply in pg reference the column, but the filterHash uses the key
        acc[camelCase(filterColumn)] = extractFromInParens.exec(filterStr)[1].split(',');
      } else {
        const [filterColumn, filterValues] = filterStr.split('=');
        const trimmedValues = filterValues.trim();
        // Why camelcase? bc the filters_to_apply in pg reference the column, but the filterHash uses the key
        const filterKey = camelCase(filterColumn);
        const isMultiselect = filterOptionHash[filterKey].filter_type === 'multiselect';
        acc[filterKey] = isMultiselect ? [trimmedValues] : trimmedValues;
      }
      return acc;
    }, {});
  }

  public getTableDataShapedToMadlib$({ fociOptions, filterOptions, groupOptions }): IFormattedMadlibOptions {
    const fociDropdownOptions = this.shapeTableDataToDropdown(fociOptions);
    const filterDropdownOptions = this.shapeFiltersToSuperFilter(filterOptions);
    const groupingDropdownOptions = this.shapeTableDataToDropdown(groupOptions);
    return { fociDropdownOptions, filterDropdownOptions, groupingDropdownOptions };
  }

  public shapeFiltersToSuperFilter(rawFilterData: Array<INetworkMidLevelFilterOption>) {
    return rawFilterData.reduce(
      (formattedFilters, filter: INetworkMidLevelFilterOption) => {
        const { category, filter_type, filter_values } = filter;
        if (filter_type === 'range')
          formattedFilters[camelCase(category)].push({
            ...filter,
            ...this.getRangeOptions(filter_values as Array<number>),
          });
        else formattedFilters[camelCase(category)].push({ ...filter, filter_search_text: new FormControl() });
        return formattedFilters;
      },
      { studentFilter: [], schoolFilter: [] },
    );
  }

  public shapeTableDataToDropdown(tableData) {
    return tableData.reduce((formattedData: Array<any>, { key, category, category_order, label: human }) => {
      if (!formattedData[category_order - 1]) formattedData.push({ key: category, human: category, options: [] });
      formattedData[category_order - 1].options.push({ key, human });
      return formattedData;
    }, []);
  }

  public getOptionHash(options) {
    return options.reduce((hash, option) => {
      hash[option.key] = option;
      return hash;
    }, {});
  }

  public getInitialFilters(madlibData: IRawNetworkMadlibData): { [key: string]: Array<string> } {
    return madlibData.filterOptions.reduce((acc, { key, filter_type, filter_values }) => {
      acc[key] = filter_type === 'multiselect' ? [...filter_values] : null;
      return acc;
    }, {});
  }

  private getRangeOptions([min, max]: Array<number>) {
    const rangeForm = new FormGroup({ sliderControl: new FormControl([min, max]) });
    const rangeOptions = {
      floor: min,
      ceil: max,
      step: 1,
      hideLimitLabels: true,
      boundPointerLabels: false,
    };
    return { rangeForm, rangeOptions };
  }

  private getNetworkGroupDataPayload(formattedFocus, formattedFilters, formattedGroup) {
    const query = `
      query NetworkMidLevel($filter: [NetworkDashMidLevelFilter]!) {
        NetworkMidLevelGrouping(focus:"${formattedFocus}", filters: $filter, group: "${formattedGroup}") {
          key
          label
          tooltip
          rowData {
            columnKey
            data
            meta
          }
          columnData {
            key
            label
            cellFormat
          }
        }
      }
    `;
    return { query, fetchPolicy: 'no-cache', variables: { filter: formattedFilters } };
  }
}
